import React, { useEffect, useState } from "react";
import List from "./list";
import {
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import getArticleService from "../../../services/getArticleService";
import { getCookie } from "../../../function";
import { useLocation, useNavigate } from "react-router-dom";
import getClientService from "../../../services/getClientService";
import getJournalService from "../../../services/getJournalService";

const SubmittedArticle = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [getdata, setGetData] = useState([]);
  const [allJournals, setAllJournals] = useState([]); // ✅ All journals for dropdown
  const [clients, setClients] = useState([]); // For TL client dropdown
  const [tlClientIds, setTlClientIds] = useState([]); // IDs for TL
  const [userType, setUserType] = useState("");
  const [userId, setUserId] = useState(""); // For logged-in user

  const [filterVal, setFilterVal] = useState({
    pageNumber: 1,
    pageSize: 10,
    searchText: "",
    status: "",
    id: "",
    totalPages: 1,
    PublicationId: "",
    month: "",
  });

  const { pageNumber, pageSize, searchText, id, status, PublicationId, month } =
    filterVal;
  console.log("pageNumber", pageNumber);

  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const urlStatus = params.get("status");
  const computedStatus = urlStatus
    ? urlStatus.charAt(0).toUpperCase() + urlStatus.slice(1)
    : filterVal.status;

  // ✅ Fetch filtered articles
  const fetchData = async (customFilterVal = filterVal) => {
    try {
      let list = null;
      if (userType === "tl") {
        if (customFilterVal.id) {
          // Fetch for specific client
          list = await getArticleService.getArticleByUserId({
            ...customFilterVal,
            id: customFilterVal.id,
          });
        } else if (userId && userId !== "") {
          console.log("userId", userId);
          list = await getArticleService.getArticleByJournal({
            ...customFilterVal,
            id: userId,
          });
        } else {
          return;
        }
      } else {
        list = await getArticleService.getArticleByJournal({
          ...customFilterVal,
          id: userId,
        });
      }

      // Defensive checks
      if (!list || list.error) {
        setGetData([]);
        setFilterVal((prev) => ({ ...prev, totalPages: 1 }));
        return;
      }

      setFilterVal((prev) => ({ ...prev, totalPages: list.totalPages || 1 }));
      setGetData(list.articles || []);
    } catch (error) {
      setGetData([]);
      setFilterVal((prev) => ({ ...prev, totalPages: 1 }));
      console.error("Error fetching articles:", error);
    }
  };

  // ✅ Fetch all journals for dropdown (once)
  const fetchAllJournals = async () => {
    const userid = getCookie("id");
    console.log("userid", userid);
    try {
      const list = await getJournalService.GetjournalByUserId(userid);
      console.log("journallist", list);
      setAllJournals(list || []);
    } catch (error) {
      console.error("Error fetching journals:", error);
    }
  };

  const handleStatusChange = (event) => {
    const newStatus = event.target.value;
    // Always update local state first
    setFilterVal((prev) => ({
      ...prev,
      status: newStatus,
      pageNumber: 1,
    }));

    // Then update URL if needed
    if (urlStatus !== null) {
      params.set("status", newStatus);
      navigate({ search: params.toString() }, { replace: true });
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleJournalChange = (event) => {
    setFilterVal({
      ...filterVal,
      PublicationId: event.target.value,
      pageNumber: 1,
    });
  };

  // Add a handler for TL client dropdown
  const handleTLClientChange = (event) => {
    setFilterVal((prev) => ({
      ...prev,
      id: event.target.value,
      pageNumber: 1,
    }));
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setFilterVal((prev) => ({
        ...prev,
        searchText: searchTerm,
        pageNumber: 1,
      }));
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  useEffect(() => {
    fetchAllJournals(); // ✅ Fetch once on mount
  }, []);

  // Sync URL status with local state
  useEffect(() => {
    if (urlStatus !== null) {
      const formattedStatus =
        urlStatus.charAt(0).toUpperCase() + urlStatus.slice(1);
      setFilterVal((prev) => ({
        ...prev,
        status: formattedStatus,
      }));
    }
  }, [urlStatus]);

  // Ensure fetchData is called on mount and when id/userType changes
  useEffect(() => {
    if (userType === "tl" && !userId) return; // Wait for userId to be set for TL
    fetchData({ ...filterVal, id: filterVal.id });
    // eslint-disable-next-line
  }, [
    filterVal.id,
    userType,
    userId, // add userId as a dependency
    pageNumber,
    pageSize,
    searchText,
    status,
    PublicationId,
    month,
  ]);

  useEffect(() => {
    // Get userType and CreclientId from token
    const token = getCookie("token");
    if (token) {
      try {
        const decoded = require("jwt-decode").jwtDecode(token);
        setUserType(decoded?.user?.userType || "");
        setUserId(decoded?.user?.id || "");
        if (decoded?.user?.userType === "tl" && decoded?.user?.CreclientId) {
          // Parse CreclientId string to array
          let ids = [];
          try {
            ids = JSON.parse(decoded.user.CreclientId);
          } catch {
            ids = decoded.user.CreclientId.split(",").map((id) => id.trim());
          }
          setTlClientIds(ids);
        }
      } catch (e) {
        setUserType("");
        setUserId("");
      }
    }
  }, []);

  // Fetch all clients and filter for TL
  useEffect(() => {
    const fetchClients = async () => {
      if (userType === "tl" && tlClientIds.length > 0) {
        try {
          const allClients = await getClientService.GetClientList();
          // Only include clients whose id is in tlClientIds
          const filtered = allClients.filter((c) =>
            tlClientIds.includes(String(c.id))
          );
          setClients(filtered);
        } catch (e) {
          setClients([]);
        }
      }
    };
    fetchClients();
  }, [userType, tlClientIds]);

  return (
    <div>
      <h3>Invoice List</h3>
      <div className="my-3 d-flex justify-content-between">
        <div>
          {/* Month Filter */}
          <FormControl sx={{ m: 1, minWidth: 200 }} size="small">
            <TextField
              type="month"
              label="Select Month"
              InputLabelProps={{ shrink: true }}
              value={filterVal.month}
              onChange={(e) =>
                setFilterVal((prev) => ({
                  ...prev,
                  month: e.target.value, // Format: "2025-08"
                  pageNumber: 1,
                }))
              }
            />
          </FormControl>
          {/* Status Filter */}
          <FormControl sx={{ m: 1, minWidth: 200 }} size="small">
            <Select
              labelId="status-select-label"
              name="status"
              displayEmpty
              value={computedStatus}
              onChange={handleStatusChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Pending">Pending Paymemt</MenuItem>
              <MenuItem value="Confirmed">Confirmed Paymemt</MenuItem>
              <MenuItem value="Unconfirmed">Unconfirmed Paymemt</MenuItem>
            </Select>
          </FormControl>

          {/* Journal Filter */}
          <FormControl sx={{ m: 1, minWidth: 200 }} size="small">
            <Select
              labelId="journal-select-label"
              name="journal"
              displayEmpty
              value={PublicationId}
              onChange={handleJournalChange}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 350,
                  },
                },
              }}
            >
              <MenuItem value="">All Publications</MenuItem>
              {[...allJournals]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((j) => (
                  <MenuItem key={j.id} value={j.id}>
                    {j.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* TL Client Filter Dropdown */}
          {userType === "tl" && (
            <FormControl sx={{ m: 1, minWidth: 200 }} size="small">
              <Select
                labelId="tl-client-select-label"
                name="tl-client"
                displayEmpty
                value={filterVal.id}
                onChange={handleTLClientChange}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 350,
                    },
                  },
                }}
              >
                <MenuItem value="">All Clients</MenuItem>
                {clients
                  .sort((a, b) => a.first_name.localeCompare(b.first_name))
                  .map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {`${c.first_name} ${c.last_name}`}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}
        </div>

        {/* Search Box */}
        <FormControl
          sx={{ m: 1, width: "25ch" }}
          variant="outlined"
          size="small"
        >
          <InputLabel htmlFor="outlined-adornment-search">Search</InputLabel>
          <OutlinedInput
            id="outlined-adornment-search"
            type="text"
            placeholder="Search By Id"
            name="search"
            value={searchTerm}
            onChange={handleSearchChange}
            endAdornment={
              <InputAdornment position="end">
                <IconButton edge="end">
                  <Search />
                </IconButton>
              </InputAdornment>
            }
            label="Search"
          />
        </FormControl>
      </div>

      {/* Article List */}
      <List
        getdata={getdata}
        fetchData={fetchData}
        filterVal={filterVal}
        setFilterVal={setFilterVal}
      />
    </div>
  );
};

export default SubmittedArticle;
